import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';

import { getTokenWithType } from './useAuth';

import { getQuestionsList } from '../utils/urls';

import { Questions } from 'types/questions';
import { ErrorModal, SortItemType } from 'types/modalTypes';

const pageLength = 100;

function getColumnFromId(id: string, fromQuestionGrid?: boolean) {
  const idsList = ['Question Type', 'Field Name', 'Original Field Label'];
  const idsListForms = [
    'IsDisabled',
    'Question Type',
    'Field Name',
    'Original Field Label',
  ];
  let index;
  if (fromQuestionGrid) {
    index = idsList.findIndex(item => item === id);
  } else {
    index = idsListForms.findIndex(item => item === id);
  }
  return `${index}`;
}

async function fetchQuestionsList({
  requestData,
  pageParam,
}: TVariables): Promise<TResult> {
  requestData.append('start', `${pageParam}`);
  const response = await fetch(getQuestionsList, {
    method: 'POST',
    headers: {
      Authorization: getTokenWithType(),
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: requestData,
  });
  return response.json();
}

type TError = ErrorModal;

type TResult = {
  Data: Array<Questions>;
  Draw: number;
  FilteredRecords: number;
  IsAutoRefreshEnabled: boolean;
  TotalRecords: number;
  PageNumber: number;
  IsSuccess: boolean;
  StatusCode: number;
};

type TVariables = {
  requestData: any;
  pageParam: number;
};

function formatRequestData(
  columnsSearch: Record<string, string>,
  sortedColumn: string,
  sortOrder: string,
  keywordSearch: string,
  fromQuestionGrid?: boolean,
): FormData {
  const formData: any = new URLSearchParams();
  if (fromQuestionGrid) {
    formData.append('draw', '1');
    formData.append('columns[0][data]', 'QuestionType.Description');
    formData.append('columns[0][name]', '');
    formData.append('columns[0][searchable]', 'true');
    formData.append('columns[0][orderable]', 'true');
    formData.append(
      'columns[0][search][value]',
      columnsSearch && columnsSearch['Question Type']
        ? columnsSearch['Question Type']
        : '',
    );
    formData.append('columns[0][search][regex]', 'false');
    formData.append('columns[1][data]', 'FieldName');
    formData.append('columns[1][name]', '');
    formData.append('columns[1][searchable]', 'true');
    formData.append('columns[1][orderable]', 'true');
    formData.append(
      'columns[1][search][value]',
      columnsSearch && columnsSearch['Field Name']
        ? columnsSearch['Field Name']
        : '',
    );
    formData.append('columns[1][search][regex]', 'false');
    formData.append('columns[2][data]', 'FieldLabel');
    formData.append('columns[2][name]', '');
    formData.append('columns[2][searchable]', 'true');
    formData.append('columns[2][orderable]', 'true');
    formData.append(
      'columns[2][search][value]',
      columnsSearch && columnsSearch['Original Field Label']
        ? columnsSearch['Original Field Label']
        : '',
    );
    formData.append('columns[2][search][regex]', 'false');
    formData.append('order[0][column]', sortedColumn);
    formData.append('order[0][dir]', sortOrder);
    formData.append('search[value]', `${keywordSearch}`);
    formData.append('search[regex]', 'false');
    formData.append('length', `${100}`);
    formData.append('timezoneOffset', '-330');
    return formData;
  } else {
    formData.append('draw', '1');
    formData.append('columns[0][data]', 'IsDisabled');
    formData.append('columns[0][name]', '');
    formData.append('columns[0][searchable]', 'true');
    formData.append('columns[0][orderable]', 'true');
    formData.append('columns[0][search][value]', 'false');
    formData.append('columns[0][search][regex]', 'false');
    formData.append('columns[1][data]', 'QuestionType.Description');
    formData.append('columns[1][name]', '');
    formData.append('columns[1][searchable]', 'true');
    formData.append('columns[1][orderable]', 'true');
    formData.append(
      'columns[1][search][value]',
      columnsSearch && columnsSearch['Question Type']
        ? columnsSearch['Question Type']
        : '',
    );
    formData.append('columns[1][search][regex]', 'false');
    formData.append('columns[2][data]', 'FieldName');
    formData.append('columns[2][name]', '');
    formData.append('columns[2][searchable]', 'true');
    formData.append('columns[2][orderable]', 'true');
    formData.append(
      'columns[2][search][value]',
      columnsSearch && columnsSearch['Field Name']
        ? columnsSearch['Field Name']
        : '',
    );
    formData.append('columns[2][search][regex]', 'false');
    formData.append('columns[3][data]', 'FieldLabel');
    formData.append('columns[3][name]', '');
    formData.append('columns[3][searchable]', 'true');
    formData.append('columns[3][orderable]', 'true');
    formData.append(
      'columns[3][search][value]',
      columnsSearch && columnsSearch['Original Field Label']
        ? columnsSearch['Original Field Label']
        : '',
    );
    formData.append('columns[3][search][regex]', 'false');
    formData.append('order[0][column]', sortedColumn);
    formData.append('order[0][dir]', sortOrder);
    formData.append('search[value]', `${keywordSearch}`);
    formData.append('search[regex]', 'false');
    formData.append('length', `${100}`);
    formData.append('timezoneOffset', '-330');
    return formData;
  }
}

function useGetQuestions({
  columnsSearch,
  sortItem,
  keywordSearch,
  fromQuestionGrid,
}: {
  columnsSearch: Record<string, string>;
  sortItem: SortItemType;
  keywordSearch: string;
  fromQuestionGrid?: boolean;
}): UseInfiniteQueryResult<TResult, TError> {
  const requestData = formatRequestData(
    columnsSearch,
    getColumnFromId(sortItem.sortKey, fromQuestionGrid),
    sortItem.sortOrder,
    keywordSearch,
    fromQuestionGrid,
  );

  return useInfiniteQuery(
    ['questions_list', keywordSearch, columnsSearch, sortItem],
    ({ pageParam = 0 }) => fetchQuestionsList({ requestData, pageParam }),
    {
      getNextPageParam: (lastPage: TResult, allPages: Array<TResult>) => {
        if (lastPage !== null && lastPage?.Data?.length === pageLength)
          return allPages?.length * pageLength;
      },
      getPreviousPageParam: (firstPage: TResult, allPages: Array<TResult>) => {
        if (firstPage !== null && firstPage?.Data?.length === pageLength)
          return allPages?.length - 1 * pageLength;
      },
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}

export { useGetQuestions };
